import React, { useMemo } from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../constants/forms";
import { getFieldProps } from "../../../../utils/form-fields";

import TextInput from "../../../controls/TextInput";
import MaskInput from "../../../controls/MaskInput";
import Checkbox from "../../../controls/Checkbox";
import Select from "../../../controls/Select";

const getYearOptions = () => {
  const startYear = new Date().getFullYear() - 1900;

  return Array.from(new Array(startYear + 2), (_, i) => {
    return { value: 1900 + i, title: `${1900 + i}` };
  });
}

const StyledRow = styled(Row)`
  .col-4 {
    padding-right: 0;
  }

  .col-4 + .col-4 {
    padding-left: 8px;
    padding-right: 8px;
  }

  .col-4 + .col-4 + .col-4  {
    padding-left: 0;
  }
`;

function GeneralQuestionsBlock({ values = {}, onChange }) {
  const showPaymentFields = ["EIN_SoleProp", "EIN_LLC", "EIN_CCorp", "EIN_SCorp"].includes(values?.code);
  const showExtraFields = values?.code !== "EIN_Trust";
  const showPreviousEINFields = values[FORM_FIELD_KEYS.questionsAppliedBefore] === "true";
  const showHireFields = values[FORM_FIELD_KEYS.questionsHire] === "true";
  const showAnnualTaxesField = values[FORM_FIELD_KEYS.payLessThan4k] === "true";
  const showNumberOfHouseholdEmployeesField = values?.code !== "EIN_Partnership" || values?.code !== "EIN_PSC";
  
  const isNumberOfEmployeesInvalid = useMemo(() => {
    return (
      values[FORM_FIELD_KEYS.numberOfAgricultureEmployees] + 
      values[FORM_FIELD_KEYS.numberOfHouseholdeEmployees] + 
      values[FORM_FIELD_KEYS.numberOfOtherEmployees]
    ) < 1;
  }, [values]);

  return (
    <Row>
      {showPaymentFields && (
        <>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.acceptCreditCardPayments)}
              value={values[FORM_FIELD_KEYS.acceptCreditCardPayments]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.payEmployeesViaPayroll)}
              value={values[FORM_FIELD_KEYS.payEmployeesViaPayroll]}
              onChange={onChange}
            />
          </Col>
        </>
      )}
      {showExtraFields && (
        <>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.questionsHighway)}
              value={values[FORM_FIELD_KEYS.questionsHighway]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.questionsGambling)}
              value={values[FORM_FIELD_KEYS.questionsGambling]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.questionsATF)}
              value={values[FORM_FIELD_KEYS.questionsATF]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.questionsExcise)}
              value={values[FORM_FIELD_KEYS.questionsExcise]}
              onChange={onChange}
            />
          </Col>
        </>
      )}
      <Col xs={12}>
        <Checkbox
          {...getFieldProps(FORM_FIELD_KEYS.questionsAppliedBefore)}
          value={values[FORM_FIELD_KEYS.questionsAppliedBefore]}
          onChange={onChange}
        />
      </Col>
      {showPreviousEINFields && (
        <>
          <Col xs={12} md={6}>
            <MaskInput
              {...getFieldProps(FORM_FIELD_KEYS.previousEINNumberFirst2)}
              value={values[FORM_FIELD_KEYS.previousEINNumberFirst2]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <MaskInput
              {...getFieldProps(FORM_FIELD_KEYS.previousEINNumberLast7)}
              value={values[FORM_FIELD_KEYS.previousEINNumberLast7]}
              onChange={onChange}
            />
          </Col>
        </>
      )}
      <Col xs={12}>
        <Checkbox
          {...getFieldProps(FORM_FIELD_KEYS.questionsHire)}
          value={values[FORM_FIELD_KEYS.questionsHire]}
          onChange={onChange}
        />
      </Col>
      {showHireFields && (
        <>
          <Col xs={12}>
            <Checkbox
              {...getFieldProps(FORM_FIELD_KEYS.payLessThan4k)}
              value={values[FORM_FIELD_KEYS.payLessThan4k]}
              onChange={onChange}
            />
          </Col>
          {showAnnualTaxesField && (
            <Col xs={12}>
              <Checkbox
                {...getFieldProps(FORM_FIELD_KEYS.annualTaxes)}
                value={values[FORM_FIELD_KEYS.annualTaxes]}
                onChange={onChange}
              />
            </Col>
          )}
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.numberOfAgricultureEmployees)}
              isInvalid={isNumberOfEmployeesInvalid}
              value={values[FORM_FIELD_KEYS.numberOfAgricultureEmployees]}
              onChange={onChange}
            />
          </Col>
          {showNumberOfHouseholdEmployeesField && (
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.numberOfHouseholdeEmployees)}
                isInvalid={isNumberOfEmployeesInvalid}
                value={values[FORM_FIELD_KEYS.numberOfHouseholdeEmployees]}
                onChange={onChange}
              />
            </Col>
          )}
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.numberOfOtherEmployees)}
              isInvalid={isNumberOfEmployeesInvalid}
              value={values[FORM_FIELD_KEYS.numberOfOtherEmployees]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <StyledRow>
              <Col xs={4}>
                <Select
                  {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesMonth)}
                  value={values[FORM_FIELD_KEYS.firstDateWagesMonth]}
                  validated={values.validated}
                  onChange={onChange}
                />
              </Col>
              <Col xs={4}>
                <Select
                  {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesDay)}
                  value={values[FORM_FIELD_KEYS.firstDateWagesDay]}
                  validated={values.validated}
                  onChange={onChange}
                />
              </Col>
              <Col xs={4}>
                <Select
                  {...getFieldProps(FORM_FIELD_KEYS.firstDateWagesYear)}
                  options={getYearOptions()}
                  value={values[FORM_FIELD_KEYS.firstDateWagesYear]}
                  validated={values.validated}
                  onChange={onChange}
                />
              </Col>
            </StyledRow>
          </Col>
        </>
      )}
      <Col xs={12}>
        <div className="inputs-block-divider" />
      </Col>
    </Row>
  );
}

export default GeneralQuestionsBlock;
